import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode, type KeyboardEvent } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import './ownerKit.css'

export type TableDensity = 'comfortable' | 'compact'

export type ColumnDef<T> = {
  key: string
  header: string
  width?: number
  sortable?: boolean
  accessor?: (row: T) => string | number | boolean | null | undefined
  render?: (row: T, index: number) => ReactNode
}

export type OwnerTableProps<T> = {
  data: T[]
  columns: ColumnDef<T>[]
  viewKey?: string
  visibilityStorageKey?: string
  getRowId?: (row: T, index: number) => string
  onRowClick?: (row: T) => void
  onRowAction?: (row: T) => void
  pageSize?: number
  emptyState?: ReactNode
  className?: string
  actionsMenu?: (row: T) => ReactNode
}

const getStoredDensity = (viewKey: string): TableDensity => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return 'comfortable'
    const val = window.localStorage.getItem(`timelining_owner_table_density_${viewKey}`)
    return val === 'compact' ? 'compact' : 'comfortable'
  } catch {
    return 'comfortable'
  }
}

const setStoredDensity = (viewKey: string, density: TableDensity): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.setItem(`timelining_owner_table_density_${viewKey}`, density)
  } catch {
    // Ignore private browsing restrictions
  }
}

const getStoredColWidths = (viewKey: string): Record<string, number> => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {}
    const val = window.localStorage.getItem(`timelining_owner_table_col_widths_${viewKey}`)
    return val ? JSON.parse(val) : {}
  } catch {
    return {}
  }
}

const setStoredColWidths = (viewKey: string, widths: Record<string, number>): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.setItem(`timelining_owner_table_col_widths_${viewKey}`, JSON.stringify(widths))
  } catch {
    // Ignore
  }
}

const getStoredColVisibility = (storageKey: string): Record<string, boolean> => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {}
    const val = window.localStorage.getItem(storageKey)
    return val ? JSON.parse(val) : {}
  } catch {
    return {}
  }
}

const setStoredColVisibility = (storageKey: string, vis: Record<string, boolean>): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.setItem(storageKey, JSON.stringify(vis))
  } catch {
    // Ignore private browsing restrictions
  }
}

export function OwnerTable<T>({
  data = [],
  columns,
  viewKey = 'default',
  visibilityStorageKey,
  getRowId = (_, i) => String(i),
  onRowClick,
  onRowAction,
  pageSize = 50,
  emptyState,
  className = '',
  actionsMenu,
}: OwnerTableProps<T>) {
  const { t } = useNls()
  const [density, setDensity] = useState<TableDensity>(() => getStoredDensity(viewKey))
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => getStoredColWidths(viewKey))
  const colVisKey = visibilityStorageKey || (viewKey !== 'default' ? `timelining_owner_table_col_vis_${viewKey}` : undefined)
  const [colVisibility, setColVisibility] = useState<Record<string, boolean>>(() => {
    return colVisKey ? getStoredColVisibility(colVisKey) : {}
  })
  const [isColMenuOpen, setIsColMenuOpen] = useState(false)
  const colMenuRef = useRef<HTMLDivElement>(null)

  const handleToggleColumn = (key: string) => {
    setColVisibility((prev) => {
      const next = { ...prev, [key]: prev[key] === false ? true : false }
      if (colVisKey) setStoredColVisibility(colVisKey, next)
      return next
    })
  }

  useEffect(() => {
    if (!isColMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) {
        setIsColMenuOpen(false)
      }
    }
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setIsColMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isColMenuOpen])

  const visibleColumns = useMemo(() => {
    if (!colVisKey) return columns
    return columns.filter((col) => colVisibility[col.key] !== false)
  }, [columns, colVisibility, colVisKey])

  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState<number>(0)
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const [prevPage, setPrevPage] = useState(page)
  const [prevSortKey, setPrevSortKey] = useState(sortKey)
  const [prevSortDir, setPrevSortDir] = useState(sortDirection)
  if (prevPage !== page || prevSortKey !== sortKey || prevSortDir !== sortDirection) {
    setPrevPage(page)
    setPrevSortKey(sortKey)
    setPrevSortDir(sortDirection)
    setSelectedIndex(-1)
  }
  const resizingRef = useRef<{ colKey: string; startX: number; startWidth: number } | null>(null)

  const handleDensityChange = (newDensity: TableDensity) => {
    setDensity(newDensity)
    setStoredDensity(viewKey, newDensity)
  }

  const handleResizeStart = (colKey: string, e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    const thEl = (e.currentTarget as HTMLElement).parentElement
    const colDef = columns.find((c) => c.key === colKey)
    const currentWidth = colWidths[colKey] ?? colDef?.width ?? (thEl?.getBoundingClientRect().width || 120)
    resizingRef.current = { colKey, startX: e.clientX, startWidth: currentWidth }

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return
      const diff = moveEvent.clientX - resizingRef.current.startX
      const newWidth = Math.max(50, Math.min(800, resizingRef.current.startWidth + diff))
      setColWidths((prev) => {
        const next = { ...prev, [resizingRef.current!.colKey]: newWidth }
        return next
      })
    }

    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColWidths((latest) => {
          setStoredColWidths(viewKey, latest)
          return latest
        })
      }
      resizingRef.current = null
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }

  const handleHeaderClick = (col: ColumnDef<T>) => {
    if (!col.sortable) return
    if (sortKey === col.key) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        setSortKey(null)
      }
    } else {
      setSortKey(col.key)
      setSortDirection('asc')
    }
  }

  const sortedData = useMemo(() => {
    if (!sortKey) return data
    const col = columns.find((c) => c.key === sortKey)
    if (!col) return data

    return [...data].sort((a, b) => {
      const aVal = col.accessor ? col.accessor(a) : (a as Record<string, unknown>)[sortKey]
      const bVal = col.accessor ? col.accessor(b) : (b as Record<string, unknown>)[sortKey]

      if (aVal === bVal) return 0
      if (aVal === null || aVal === undefined) return 1
      if (bVal === null || bVal === undefined) return -1

      const result = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal))
      return sortDirection === 'asc' ? result : -result
    })
  }, [data, sortKey, sortDirection, columns])

  // Hard safety limit: Never render > 200 rows raw
  const safePageSize = Math.min(pageSize, 200)
  const totalPages = Math.ceil(sortedData.length / safePageSize) || 1
  const currentPage = Math.min(Math.max(0, page), totalPages - 1)

  const pagedData = useMemo(() => {
    const start = currentPage * safePageSize
    return sortedData.slice(start, start + safePageSize)
  }, [sortedData, currentPage, safePageSize])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTableSectionElement>) => {
      if (pagedData.length === 0) return

      const target = e.target as HTMLElement | null
      const isInteractive = Boolean(
        target &&
        target !== e.currentTarget &&
        target.closest(
          'a, button, input, textarea, select, summary, [role="button"], [role="link"], [role="checkbox"], [role="menuitem"], [role="option"], [role="switch"], [role="tab"], [contenteditable="true"], [contenteditable=""]'
        )
      )

      if (isInteractive) {
        const isEditable = Boolean(target?.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""]'))
        if (isEditable) return
        if (e.key === 'Enter' || e.key === ' ') return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, pagedData.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < pagedData.length) {
          const item = pagedData[selectedIndex]
          if (onRowClick) onRowClick(item)
          else if (onRowAction) onRowAction(item)
        }
      }
    },
    [pagedData, selectedIndex, onRowClick, onRowAction],
  )

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  return (
    <div className={`ownerTableContainer ${className}`} data-testid="owner-table">
      <div className="ownerTableToolbar">
        <div className="ownerTableToolbarLeft">
          <span className="ownerTableCount">
            {data.length === 1
              ? t('NLSOwnerConsoleCountItemOne')
              : t('NLSOwnerConsoleCountItemMany', { count: data.length })}
          </span>
        </div>
        <div className="ownerTableToolbarRight" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {colVisKey && (
            <div className="ownerTableColVisWrapper" ref={colMenuRef}>
              <button
                type="button"
                className="ownerTableDensityBtn"
                onClick={() => setIsColMenuOpen((prev) => !prev)}
                aria-expanded={isColMenuOpen}
                aria-label={t('NLSOwnerConsoleTableColumns')}
              >
                {t('NLSOwnerConsoleTableColumns')}
              </button>
              {isColMenuOpen && (
                <div className="ownerTableColVisMenu">
                  {columns.map((col) => {
                    const isVisible = colVisibility[col.key] !== false
                    return (
                      <label key={col.key} className="ownerTableColVisOption">
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => handleToggleColumn(col.key)}
                        />
                        <span>{col.header}</span>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          <div className="ownerTableDensitySelector" role="group" aria-label={t('NLSOwnerConsoleAriaTableDensity')}>
            <button
              type="button"
              className={`ownerTableDensityBtn ${density === 'comfortable' ? 'ownerTableDensityBtn--active' : ''}`}
              onClick={() => handleDensityChange('comfortable')}
            >
              {t('NLSOwnerConsoleDensityComfortable')}
            </button>
            <button
              type="button"
              className={`ownerTableDensityBtn ${density === 'compact' ? 'ownerTableDensityBtn--active' : ''}`}
              onClick={() => handleDensityChange('compact')}
            >
              {t('NLSOwnerConsoleDensityCompact')}
            </button>
          </div>
        </div>
      </div>

      <div className="ownerTableScrollWrapper">
        <table className={`ownerTable ownerTable--${density}`}>
          <thead>
            <tr>
              {visibleColumns.map((col, index) => {
                const colWidth = colWidths[col.key] ?? col.width
                return (
                  <th
                    key={col.key}
                    style={colWidth ? { width: colWidth, minWidth: colWidth } : undefined}
                    className={`${index === 0 ? 'ownerTableStickyCol' : ''} ${col.sortable ? 'ownerTableSortable' : ''}`}
                    onClick={() => handleHeaderClick(col)}
                    aria-sort={sortKey === col.key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <span>{col.header}</span>
                    {col.sortable && (
                      <span className={`ownerTableSortIcon ${sortKey === col.key ? 'ownerTableSortIcon--active' : ''}`}>
                        {sortKey === col.key ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                    )}
                    <div
                      className="ownerTableColResizer"
                      onMouseDown={(e) => handleResizeStart(col.key, e)}
                      onClick={(e) => e.stopPropagation()}
                      role="separator"
                      aria-orientation="vertical"
                    />
                  </th>
                )
              })}
              {actionsMenu && <th style={{ width: 44 }} aria-label={t('NLSOwnerConsoleActions')} />}
            </tr>
          </thead>
          <tbody tabIndex={0} onKeyDown={handleKeyDown}>
            {pagedData.map((row, index) => {
              const rowId = getRowId(row, index)
              const isSelected = selectedIndex === index

              return (
                <tr
                  key={rowId}
                  className={`ownerTableRow ${isSelected ? 'ownerTableRow--selected' : ''}`}
                  onClick={() => {
                    setSelectedIndex(index)
                    if (onRowClick) onRowClick(row)
                  }}
                  data-row-id={rowId}
                >
                  {visibleColumns.map((col, colIndex) => {
                    const colWidth = colWidths[col.key] ?? col.width
                    return (
                      <td
                        key={col.key}
                        className={colIndex === 0 ? 'ownerTableStickyCol' : ''}
                        style={colWidth ? { width: colWidth, minWidth: colWidth } : undefined}
                      >
                        {col.render
                          ? col.render(row, index)
                          : String(col.accessor ? col.accessor(row) ?? '' : (row as Record<string, unknown>)[col.key] ?? '')}
                      </td>
                    )
                  })}
                  {actionsMenu && (
                    <td style={{ width: 44, textAlign: 'right' }}>
                      {actionsMenu(row)}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="ownerTablePagination">
          <span>
            {t('NLSOwnerConsolePageOf', { current: currentPage + 1, total: totalPages })}
          </span>
          <div className="ownerTablePaginationButtons">
            <button
              type="button"
              className="ownerTablePaginationBtn"
              disabled={currentPage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              {t('NLSOwnerConsolePrevious')}
            </button>
            <button
              type="button"
              className="ownerTablePaginationBtn"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              {t('NLSOwnerConsoleNext')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
